import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ProtectedLayout } from 'hoc';
import { AudioUploader, PatientHeader, PatientHeaderSkeleton, PausedConsultations } from 'widgets';
import { AudioRecordingConsent } from 'widgets/recording/AudioRecordingConsent';
import { PastConsultations } from 'widgets/patientOverview/PastConsultations';
import { NO_RECORDING_SELECTION, PatientOverviewFooter } from 'widgets/patientOverview';
import { BillingScheinAlert } from 'widgets/billingSchein';
import { useAnalytics } from 'analytics';
import { ANALYTICS_EVENTS } from 'analytics/events';
import { useMe } from 'hooks/auth';
import { useAnamnesisLookup } from 'hooks/useAnamnesisLookup';
import { useLanguages } from 'hooks/languages';
import { usePatientLoadTimeTracking } from 'hooks/usePatientLoadTimeTracking';
import { useLastRecordingLanguage } from 'hooks/useLastRecordingLanguage';
import { usePausedConsultationNavigation } from 'hooks/usePausedConsultationNavigation';
import { usePausedConsultations } from 'hooks/usePausedConsultations';
import { useRecordingObjectionStatus } from 'hooks/useRecordingObjectionStatus';
import { trackError } from 'utils/trackError';
import { buildPatientSubline, formatPatientName } from 'utils/visitReasonDisplay';
import { Container, Layout } from './patientOverview.styles';

const DEFAULT_LANGUAGE_CODE = 'de';

export default function PatientOverviewScreen() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();
  const { medatixxPatientId } = useParams<{ medatixxPatientId: string }>();
  const { data: user } = useMe();
  const {
    data: anamnesis,
    isLoading: isAnamnesisLoading,
    error: anamnesisLookupError,
  } = useAnamnesisLookup(medatixxPatientId);
  usePatientLoadTimeTracking(medatixxPatientId, {
    isLoading: isAnamnesisLoading,
    isError: !!anamnesisLookupError,
  });
  const {
    data: languagesData,
    isLoading: isLanguagesLoading,
    isLoadingError: isLanguagesError,
    error: languagesError,
  } = useLanguages();
  const {
    language: lastRecordingLanguage,
    isFetched: isLastRecordingLanguageFetched,
    isLoadingError: isLastRecordingLanguageError,
    error: lastRecordingLanguageError,
  } = useLastRecordingLanguage(medatixxPatientId);
  const [manualLanguageCode, setManualLanguageCode] = useState<string | null>(null);
  const { data: recordingObjection } = useRecordingObjectionStatus(medatixxPatientId);
  const isRecordingRestricted = recordingObjection === undefined || recordingObjection.active;
  const { consultations: pausedConsultations, isLoading: isPausedConsultationLoading } =
    usePausedConsultations({ medatixxPatientId }, { enabled: !!medatixxPatientId });
  const pausedConsultation = isRecordingRestricted
    ? undefined
    : (pausedConsultations.find((consultation) => consultation.isOwn) ?? pausedConsultations[0]);
  const { resume: resumePausedConsultation } = usePausedConsultationNavigation();

  // Guards against a route-param change reusing this screen instance without remounting —
  // a manual pick made for one patient must never carry over to the next.
  useEffect(() => {
    setManualLanguageCode(null);
  }, [medatixxPatientId]);

  const hasAnamnesis = !!anamnesis;
  useEffect(() => {
    if (!anamnesisLookupError) return;
    trackError(anamnesisLookupError, { context: 'patientOverviewAnamnesisLookup' });
    if (!hasAnamnesis) toast.error(t('patientOverview.anamnesisLoadError'));
  }, [anamnesisLookupError, hasAnamnesis, t]);

  useEffect(() => {
    if (languagesError) trackError(languagesError, { context: 'patientOverviewLanguagesLookup' });
  }, [languagesError]);

  useEffect(() => {
    if (lastRecordingLanguageError) {
      trackError(lastRecordingLanguageError, {
        context: 'patientOverviewLastRecordingLanguageLookup',
      });
    }
  }, [lastRecordingLanguageError]);

  const smartDefaultLanguageCode = useMemo(() => {
    const availableLanguages = languagesData?.languages ?? [];
    const hasRecordingHistory =
      !!lastRecordingLanguage &&
      availableLanguages.some((language) => language.code === lastRecordingLanguage);

    return (
      (hasRecordingHistory ? lastRecordingLanguage : undefined) ??
      languagesData?.defaultLanguageCode ??
      DEFAULT_LANGUAGE_CODE
    );
  }, [languagesData, lastRecordingLanguage]);

  const selectedLanguageCode =
    manualLanguageCode ??
    (user?.startWithoutRecordingByDefault ? NO_RECORDING_SELECTION : smartDefaultLanguageCode);
  const uploadLanguageCode =
    selectedLanguageCode === NO_RECORDING_SELECTION
      ? smartDefaultLanguageCode
      : selectedLanguageCode;

  // Until the user overrides it, the smart default above is only a guess — don't let
  // "Gespräch aufzeichnen" commit to it while the real answer is still in flight or failed.
  const isUsingSmartDefault = manualLanguageCode === null;
  const isLastRecordingLanguagePending = !!medatixxPatientId && !isLastRecordingLanguageFetched;
  const isResolvingLanguage =
    isUsingSmartDefault && (isLanguagesLoading || isLastRecordingLanguagePending);
  const hasLanguageResolutionError =
    isUsingSmartDefault && (isLanguagesError || isLastRecordingLanguageError);
  const canUploadAudio =
    window.environment !== 'production' &&
    !!medatixxPatientId &&
    !isAnamnesisLoading &&
    !isRecordingRestricted &&
    !isPausedConsultationLoading &&
    !pausedConsultation;

  const titleBarInfo = useMemo(() => {
    if (!anamnesis) return { title: t('patientOverview.title') };
    return {
      title: formatPatientName(anamnesis.patientName),
      subtitle: buildPatientSubline(anamnesis.birthdate, anamnesis.medatixxPatientId),
    };
  }, [anamnesis, t]);

  const handleLanguageChange = (languageCode: string) => {
    trackEvent(ANALYTICS_EVENTS.RECORDING_LANGUAGE_CHANGED, {
      previousLanguageCode: selectedLanguageCode,
      newLanguageCode: languageCode,
    });
    setManualLanguageCode(languageCode);
  };

  const openRecording = (event: string, state: Record<string, unknown>) => {
    if (!medatixxPatientId) return;
    trackEvent(event);
    void navigate(`/recording/${encodeURIComponent(medatixxPatientId)}`, { state });
  };

  const handleStartWithoutRecording = () =>
    openRecording(ANALYTICS_EVENTS.PATIENT_START_WITHOUT_RECORDING, { withoutRecording: true });

  const handleStartRecording = () =>
    openRecording(ANALYTICS_EVENTS.PATIENT_START_RECORDING, {
      languageCode: selectedLanguageCode,
      fromPatientOverview: true,
    });

  return (
    <ProtectedLayout
      title={titleBarInfo.title}
      subtitle={titleBarInfo.subtitle}
      hasBackButton={false}
      fillViewport
    >
      <Layout>
        <Container role="region" aria-label={t('patientOverview.title')} data-ph-no-autocapture>
          {isAnamnesisLoading ? (
            <PatientHeaderSkeleton />
          ) : (
            <PatientHeader
              anamnesis={anamnesis}
              showPreventionAlerts
              collapsibleSummary={!!anamnesis?.summary?.seen}
            />
          )}
          {medatixxPatientId && <PausedConsultations medatixxPatientId={medatixxPatientId} />}
          <PastConsultations medatixxPatientId={medatixxPatientId} />
          {canUploadAudio && (
            <AudioUploader
              startOptions={{
                languageCode: uploadLanguageCode,
                anamnesisId: anamnesis?.summary?.id,
                visitReasonId: anamnesis?.visitReason?.id,
                medatixxId: medatixxPatientId,
                fullname: anamnesis?.patientName,
                birthdate: anamnesis?.birthdate,
              }}
            />
          )}
        </Container>
        <BillingScheinAlert medatixxPatientId={medatixxPatientId} />
        <PatientOverviewFooter
          selectedLanguageCode={selectedLanguageCode}
          languagesData={languagesData}
          lastUsedLanguageCode={lastRecordingLanguage}
          isResolvingLanguage={isResolvingLanguage}
          hasLanguageError={hasLanguageResolutionError}
          isRecordingRestricted={isRecordingRestricted}
          isLoadingPatient={isAnamnesisLoading}
          isCheckingPausedConsultation={isPausedConsultationLoading}
          resume={
            pausedConsultation && {
              languageCode: pausedConsultation.language,
              onResume: () => resumePausedConsultation(pausedConsultation),
            }
          }
          onLanguageChange={handleLanguageChange}
          onStartRecording={handleStartRecording}
          onStartWithoutRecording={handleStartWithoutRecording}
          onBackToOverview={() => void navigate('/home')}
        >
          {!pausedConsultation && <AudioRecordingConsent medatixxPatientId={medatixxPatientId} />}
        </PatientOverviewFooter>
      </Layout>
    </ProtectedLayout>
  );
}
